import {
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Container,
  Paper,
  Stack,
  Tab,
  Tabs,
  Toolbar,
} from '@mui/material';
import { NavLink, Outlet, useLocation } from 'react-router';
import { AdSlot } from '@/components/AdSlot';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { FaiscaCredito } from '@/components/brand/FaiscaCredito';
import { ColorModeToggle } from '@/components/ColorModeToggle';
import { PageTransition } from '@/components/PageTransition';
import { brand } from '@/config/brand';
import { ConsentFooter } from '@/platform/ConsentFooter';
import { useSession } from '@/platform/session';
import { AccountMenu } from './AccountMenu';
import { InstitutionSwitcher } from './InstitutionSwitcher';
import { activeNavItem, buildNav } from './navigation';

export function AppLayout() {
  const { pathname } = useLocation();
  const session = useSession();
  const navItems = buildNav(session);
  const current = activeNavItem(navItems, pathname);
  // A single entry (e.g. a student with no module on) needs no navigation bar.
  const showNav = navItems.length > 1;

  return (
    <Box sx={{ minHeight: '100dvh', pb: { xs: showNav ? 11 : 0, md: 0 } }}>
      <AppBar position="sticky">
        <Toolbar sx={{ gap: 3, minHeight: { xs: 60, sm: 64 } }}>
          {brand.needsSetup ? (
            // Placeholder is itself a button (opens the brand guide), so no link around it.
            <BrandLogo size="small" />
          ) : (
            <NavLink
              to="/"
              style={{ textDecoration: 'none', color: 'inherit' }}
              aria-label="Início"
            >
              <BrandLogo size="small" />
            </NavLink>
          )}
          <Tabs
            value={current}
            component="nav"
            aria-label="Navegação principal"
            sx={{
              display: { xs: 'none', md: showNav ? 'flex' : 'none' },
              minHeight: 64,
              '& .MuiTab-root': { minHeight: 64 },
            }}
          >
            {navItems.map((item) => (
              <Tab
                key={item.to}
                value={item.to}
                label={item.label}
                component={NavLink}
                to={item.to}
              />
            ))}
          </Tabs>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ ml: 'auto', alignItems: 'center', minWidth: 0 }}
          >
            <InstitutionSwitcher />
            <ColorModeToggle />
            <AccountMenu />
          </Stack>
        </Toolbar>
      </AppBar>

      <Container component="main" maxWidth="lg" sx={{ pt: { xs: 3, md: 4 }, pb: 5 }}>
        <PageTransition>
          <Outlet />
        </PageTransition>
        <AdSlot />
      </Container>
      <ConsentFooter />
      <FaiscaCredito sx={{ mt: 0 }} />

      <Paper
        component="nav"
        aria-label="Navegação principal"
        sx={{
          display: { xs: showNav ? 'block' : 'none', md: 'none' },
          position: 'fixed',
          insetInline: 0,
          bottom: 0,
          zIndex: 'appBar',
          borderRadius: 0,
        }}
      >
        <BottomNavigation value={current} showLabels>
          {navItems.map((item) => (
            <BottomNavigationAction
              key={item.to}
              value={item.to}
              label={item.label}
              icon={<item.icon />}
              component={NavLink}
              to={item.to}
            />
          ))}
        </BottomNavigation>
      </Paper>
    </Box>
  );
}
